import makeStyles from "@material-ui/core/styles/makeStyles";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InputLabel from "@material-ui/core/InputLabel";
import { connect } from "react-redux";
import { getReferenceDataType } from "../../reducers";
import usersApi from "../../services/api/users";
import Avatar from "../common/Avatar.tsx";
import ChoiceChips from "../common/ChoiceChips";
import DebouncedTextField from "../common/DebouncedTextField";
import FiltersBar from "../common/FiltersBar";
import RefMultiSelect from "../common/RefMultiSelect";
import ListPicker from "../ListPicker";
import { useAuth0 } from "@auth0/auth0-react";

const statusMap = {
  OPEN: "Open",
  CLOSED: "Closed",
};
const statusOptions = ["OPEN", "CLOSED"].map((value) => ({
  label: statusMap[value],
  value,
}));

const dueDateMap = {
  overdue: "Overdue",
  thisWeek: "Week",
  thisMonth: "Month",
  thisQuarter: "Quarter",
  thisYear: "Calendar year",
};
const dueDateOptions = ["thisWeek", "thisMonth", "thisQuarter", "thisYear"].map(
  (value) => ({
    label: dueDateMap[value],
    value,
  })
);
const overdueDateOptions = ["overdue"].map((value) => ({
  label: dueDateMap[value],
  value,
}));

const useStyles = makeStyles((theme) => ({
  filterWidget: {
    marginTop: theme.spacing(2),
  },
  searchField: {
    fontSize: "0.8125rem",
    lineHeight: "1.1875em",
    minWidth: "400px",
  },
  outcomeField: {
    minWidth: "250px",
  },
}));

const RiskControlVerificationFilters = ({
  filter,
  definition,
  updateFilter,
  allOutcomes,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const classes = useStyles();
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [selectedOwnedBy, setSelectedOwnedBy] = useState([]);

  const { ownerRoles, outcomes } = definition;

  const verificationOutcomes = {
    ids: [],
    values: [],
  };
  _.uniq(
    outcomes.map((outcomeAssignment) => outcomeAssignment.outcome)
  ).forEach((outcome) => {
    verificationOutcomes.ids.push(outcome);
    verificationOutcomes.values[outcome] = allOutcomes.values[outcome];
  });

  useEffect(() => {
    (async () => {
      const accessToken = await getAccessTokenSilently();
      // initially load the users currently selected in the filter
      if (filter.ownerIds && filter.ownerIds.length > 0) {
        usersApi
          .search(
            {
              limit: filter.ownerIds.length,
              offset: 0,
              roles: ownerRoles,
              userIds: filter.ownerIds,
            },
            null,
            accessToken
          )
          .then((response) => setSelectedOwnedBy(response.results));
      }
    })();
  }, [getAccessTokenSilently]);

  const setOwnedBy = (users) => {
    setSelectedOwnedBy(users);
    updateFilter(
      "ownerIds",
      users.map((user) => user.id)
    );
    setOwnerPickerOpen(false);
  };

  const asyncFetch = async (pickerFilter, pagination, abortController) => {
    const accessToken = await getAccessTokenSilently();
    const searchParameters = {
      ...pickerFilter,
      limit: pagination.pageSize,
      offset: pagination.offset,
      roles: ownerRoles,
      orderByField: "name",
    };
    return usersApi.search(searchParameters, abortController, accessToken);
  };

  return (
    <>
      <ListPicker
        data-cy="select"
        title="Select owners"
        actionText="Select"
        open={ownerPickerOpen}
        onClose={() => setOwnerPickerOpen(false)}
        onSubmit={(u) => setOwnedBy(u)}
        datasource={asyncFetch}
        selected={selectedOwnedBy}
        isMulti
        clearable
        toOption={(user) => ({
          label: user.name || "Unknown",
          id: user.id,
          email: user.email,
        })}
        fromOption={(option) => ({
          name: option.label,
          id: option.id,
          email: option.email,
        })}
        renderIcon={(user, size) => (
          <Avatar
            data-cy={user.name}
            email={user.email}
            name={user.name}
            size={size}
            round
          />
        )}
      />
      <FiltersBar>
        <>
          <InputLabel data-cy="search">Search</InputLabel>
          <div className={classes.filterWidget}>
            <DebouncedTextField
              data-cy={filter.textSearch || ""}
              value={filter.textSearch || ""}
              onChange={(text) => updateFilter("textSearch", text)}
              placeholder="Type to filter"
              margin="none"
              InputProps={{
                className: classes.searchField,
              }}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="ownership">Ownership</InputLabel>
          <div className={classes.filterWidget}>
            <ChoiceChips
              data-cy={filter.ownerIds.length > 0 ? "ownerIds" : null}
              value={filter.ownerIds.length > 0 ? "ownerIds" : null}
              options={[
                {
                  label: "Owned by",
                  value: "ownerIds",
                  badgeValue: filter.ownerIds.length,
                },
              ]}
              onChange={() => setOwnerPickerOpen(true)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="status">Status</InputLabel>
          <div className={classes.filterWidget}>
            <ChoiceChips
              data-cy={filter.status}
              value={filter.status}
              options={statusOptions}
              onChange={(value) => updateFilter("status", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="dueThis">Due this</InputLabel>
          <div className={classes.filterWidget}>
            <ChoiceChips
              data-cy={filter.dueDate}
              value={filter.dueDate}
              options={dueDateOptions}
              onChange={(value) => updateFilter("dueDate", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="overDue">Overdue</InputLabel>
          <div className={classes.filterWidget}>
            <ChoiceChips
              data-cy={filter.dueDate}
              value={filter.dueDate}
              options={overdueDateOptions}
              onChange={(value) => updateFilter("dueDate", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="outcome">Outcome</InputLabel>
          <div className={classes.outcomeField}>
            <div className={classes.filterWidget}>
              <RefMultiSelect
                data-cy={filter.outcomes}
                title="Select outcomes"
                value={filter.outcomes}
                options={verificationOutcomes}
                onChange={(value) => updateFilter("outcomes", value)}
              />
            </div>
          </div>
        </>
      </FiltersBar>
    </>
  );
};

RiskControlVerificationFilters.propTypes = {
  filter: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
  allOutcomes: PropTypes.object.isRequired,
  definition: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  allOutcomes: getReferenceDataType(state, "WorkItemOutcome"),
  definition: getReferenceDataType(state, "WorkItemType").values
    .RISK_CONTROL_VERIFICATION.props.definition,
});

export default connect(mapStateToProps)(RiskControlVerificationFilters);
